/* Helper modal listing what a candidate can check when their mic records no
   audio. Deliberately advice-only: the mic test itself lives on the screen
   behind this modal, so repeating a live tester here would duplicate it.

   Opened from the "need help" link under the no-audio error in MicTest, never
   automatically. Dismissible — no-audio is recoverable and the candidate needs
   to get back to the test. */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobileDevice } from "hooks";
import { Cross, Dropdown, MicrophoneIcon } from "assets/icons";
import UnberryModal from "../UnberryModal";
/* Reuses the permission-flow screenshot: it already shows the browser dropdown
   with the Microphone toggle, which is exactly what the first tip describes. */
import MicPermissionImage from "../../../assets/img/permission/cam-mic-permission-tab-image-2.png";
import IncognitoImage from "../../../assets/img/mic-troubleshoot/incognito.png";
import "./style.scss";

// Screenshots keyed by tip index. Tips without an entry render as text only.
const TIP_IMAGES = {
  0: MicPermissionImage,
  4: IncognitoImage,
};

// Indices whose screenshot is tall and portrait. On mobile these fill the card
// width and crop from the bottom rather than shrinking to an unreadable
// thumbnail — see .is-tall in style.scss.
const TALL_TIP_IMAGES = [0];

const AudioTroubleshootModal = ({ visible = false, onClose }) => {
  const isMobile = useIsMobileDevice();
  const { t } = useTranslation();
  // Indices of tips whose screenshot the candidate has opened. Mobile only —
  // on desktop there is room to show every screenshot inline.
  const [expanded, setExpanded] = useState([]);

  const tips = t("mic_test.troubleshoot.tips", { returnObjects: true });
  const tipList = Array.isArray(tips) ? tips : [];

  const toggleImage = (index) => {
    setExpanded((current) =>
      current.includes(index)
        ? current.filter((i) => i !== index)
        : [...current, index]
    );
  };

  /* Desktop always shows the screenshot; on mobile it opens on request. The
     CSS hides the toggle above the tablet breakpoint, so this only has to
     account for the collapsed mobile case. */
  const isImageOpen = (index) => !isMobile || expanded.includes(index);

  return (
    <UnberryModal
      visible={visible}
      onCancel={onClose}
      footer={false}
      closable
      closeIcon={<Cross />}
      keyboard
      maskClosable
      className="audio-troubleshoot-modal"
      /* Heavier mask blur than the other modals: the panel itself is
         translucent, so the page behind has to be pushed further back for the
         text to stay legible. */
      maskStyle={{ backdropFilter: "blur(8px)", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="audio-troubleshoot-body">
        <div className="troubleshoot-header">
          <span className="header-icon" aria-hidden="true">
            <MicrophoneIcon />
          </span>
          <div className="header-copy">
            <h2 className={isMobile ? "size-16" : "size-18"}>
              {t("mic_test.troubleshoot.title")}
            </h2>
            <p className="size-14">{t("mic_test.troubleshoot.subtext")}</p>
          </div>
        </div>

        <div className="troubleshoot-tips">
          <p className="tips-heading size-12">
            {t("mic_test.troubleshoot.tips_heading")}
          </p>
          <ul className="tips-list">
            {tipList.map((tip, index) => (
              <li key={index} className="size-14">
                <span className="tip-body">
                  <span className="tip-lead">{tip.lead}</span>
                  {tip.rest ? <span className="tip-rest"> {tip.rest}</span> : null}
                  {TIP_IMAGES[index] ? (
                    <>
                      {/* Mobile only: the screenshot stays collapsed so five
                          cards fit without scrolling. Desktop shows it
                          inline, so this control is hidden there. */}
                      <button
                        type="button"
                        className={`tip-toggle size-12 ${isImageOpen(index) ? "is-open" : ""}`}
                        aria-expanded={isImageOpen(index)}
                        onClick={() => toggleImage(index)}
                      >
                        {t("mic_test.troubleshoot.show_me_how")}
                        <span className="toggle-caret" aria-hidden="true">
                          <Dropdown />
                        </span>
                      </button>
                      <span
                        className={`tip-image ${isImageOpen(index) ? "is-open" : ""} ${
                          TALL_TIP_IMAGES.includes(index) ? "is-tall" : ""
                        }`}
                      >
                        <img src={TIP_IMAGES[index]} alt="" />
                      </span>
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="troubleshoot-actions">
          <button className="dismiss-btn size-14" onClick={onClose}>
            {t("mic_test.troubleshoot.dismiss")}
          </button>
        </div>
      </div>
    </UnberryModal>
  );
};

export default AudioTroubleshootModal;
